import { useMemo } from 'react';
import { RankedBars } from '../../charts/RankedBars';
import { Card, ChartCard, DataTable, Stat, StatusChip, type Column } from '../../components/data';
import { ErrorBanner, Loading } from '../../components/ui';
import { formatCompactCurrency, num, relativeDays, snakeLabel, urgency } from '../../lib/format';
import { useResource } from '../../lib/hooks';
import {
  assetBand,
  type AssetRow,
  type ClientOption,
  type DepotData,
  type PurchaseOrderOption,
  ProductOption,
} from './adminTypes';
import { RegisterPanel, formatIsoShort, type RegisterSpec } from './adminUi';

const ASSET_CATEGORY = [
  'laptop', 'desktop', 'server', 'network', 'firewall', 'peripheral', 'phone', 'spare_part',
] as const;
const ASSET_CONDITION = ['new', 'refurbished', 'used_good', 'used_fair', 'faulty'] as const;
const ASSET_STATUS = [
  'in_stock', 'reserved', 'staged', 'deployed', 'rma', 'awaiting_disposal', 'retired',
] as const;

/**
 * Depot inventory.
 *
 * `Quantity` is the field that makes this register honest, and every figure on
 * this tab multiplies by it. One row can be a single laptop or forty patch
 * leads, and a stock count that counted ROWS would report the forty as one — see
 * the note on that column in migration 048.
 *
 * "Never audited" is counted as overdue rather than excluded. It is the same
 * problem — nobody has laid eyes on it — and excluding it would make an
 * untouched depot look perfectly audited, which is the exact opposite of true.
 */
export function AdminDepot({ canWrite, onChanged }: { canWrite: boolean; onChanged: () => void }) {
  const analytics = useResource<DepotData>('/admin/analytics/depot');
  const clients = useResource<ClientOption[]>('/admin/pickers/clients');
  const orders = useResource<PurchaseOrderOption[]>('/admin/pickers/purchase-orders');
  const products = useResource<ProductOption[]>('/admin/pickers/products');

  const spec = useMemo<RegisterSpec<AssetRow>>(
    () => ({
      resource: 'assets',
      title: 'Depot stock',
      singular: 'Asset',
      emptyTitle: 'Nothing in the depot yet',
      emptyBody:
        'Record what is on the shelf: spare laptops, staged kit waiting on an install, switches, firewalls, consumables. Quantity matters — one row can be forty patch leads, and every value and unit figure on this tab multiplies by it.',
      searchHint: 'Tag, serial, model, bin or client',
      idOf: (row) => row.DepotAssetId,
      labelOf: (row) => row.AssetTag,
      filters: [
        { param: 'status', label: 'Status', options: ASSET_STATUS },
        { param: 'category', label: 'Category', options: ASSET_CATEGORY },
        { param: 'condition', label: 'Condition', options: ASSET_CONDITION },
      ],
      columns: [
        {
          key: 'AssetTag',
          label: 'Asset',
          render: (row) => (
            <div>
              <div className="admin-cell-title">{row.AssetTag}</div>
              <div className="xs muted">
                {[row.Manufacturer, row.Model].filter(Boolean).join(' ') || snakeLabel(row.Category)}
                {row.SerialNumber ? ` · ${row.SerialNumber}` : ''}
              </div>
            </div>
          ),
        },
        {
          key: 'Status',
          label: 'Status',
          render: (row) => (
            <div className="admin-due">
              <StatusChip band={assetBand(row.Status)}>{snakeLabel(row.Status)}</StatusChip>
              {row.Condition === 'faulty' && <StatusChip band="serious">faulty</StatusChip>}
            </div>
          ),
        },
        {
          key: 'DepotLocation',
          label: 'Where',
          render: (row) => (
            <div>
              <div>{row.DepotLocation ?? <span className="sub">unassigned</span>}</div>
              {row.Bin && <div className="xs muted">bin {row.Bin}</div>}
              {row.AssignedClientName && (
                <div className="xs muted">
                  for {row.AssignedClientName}
                  {row.AssignedUserName ? ` · ${row.AssignedUserName}` : ''}
                </div>
              )}
            </div>
          ),
        },
        { key: 'Quantity', label: 'Qty', align: 'right' },
        {
          key: 'LineValue',
          label: 'Value',
          align: 'right',
          /* The line value, not the unit cost — see the note above. */
          render: (row) => (
            <div>
              <div>{formatCompactCurrency(row.LineValue)}</div>
              {row.Quantity !== 1 && (
                <div className="xs muted">{formatCompactCurrency(row.UnitCost)} each</div>
              )}
            </div>
          ),
        },
        {
          key: 'WarrantyExpiry',
          label: 'Warranty',
          render: (row) =>
            row.WarrantyExpiry ? (
              <div className="admin-due">
                <span>{formatIsoShort(row.WarrantyExpiry)}</span>
                <StatusChip band={urgency(row.DaysToWarrantyExpiry)}>
                  {relativeDays(row.DaysToWarrantyExpiry)}
                </StatusChip>
              </div>
            ) : (
              <span className="sub">—</span>
            ),
        },
        {
          key: 'LastAudited',
          label: 'Audited',
          render: (row) =>
            row.LastAudited ? (
              <div>
                <div>{formatIsoShort(row.LastAudited)}</div>
                {row.DaysSinceAudit !== null && row.DaysSinceAudit > 90 && (
                  <div className="xs admin-warn-text">{row.DaysSinceAudit}d ago</div>
                )}
              </div>
            ) : (
              <span className="admin-warn-text">never</span>
            ),
        },
      ],
      fields: [
        { name: 'assetTag', label: 'Asset tag', kind: 'text' },
        { name: 'category', label: 'Category', kind: 'select', options: ASSET_CATEGORY },
        {
          /* Optional, and Manufacturer/Model stay: plenty of what is on the
             shelf was never a catalogue line. See migration 102. */
          name: 'productId',
          label: 'Catalogue line',
          kind: 'select',
          optional: true,
          placeholder: 'Not in the price book',
          choices: (products.data ?? []).map((p) => ({
            value: String(p.ProductId),
            label: p.Category ? `${p.Name} · ${p.Category}` : p.Name,
          })),
        },
        { name: 'manufacturer', label: 'Manufacturer', kind: 'text' },
        { name: 'model', label: 'Model', kind: 'text' },
        { name: 'serialNumber', label: 'Serial number', kind: 'text' },
        { name: 'condition', label: 'Condition', kind: 'select', options: ASSET_CONDITION },
        { name: 'status', label: 'Status', kind: 'select', options: ASSET_STATUS },
        { name: 'depotLocation', label: 'Depot location', kind: 'text', hint: 'e.g. Rack B / Shelf 3' },
        { name: 'bin', label: 'Bin', kind: 'text' },
        {
          name: 'assignedClientId',
          label: 'Assigned client',
          kind: 'select',
          optional: true,
          placeholder: 'Unassigned',
          choices: (clients.data ?? []).map((c) => ({
            value: String(c.ClientId),
            label: c.IsActive ? c.Name : `${c.Name} (inactive)`,
          })),
        },
        {
          name: 'assignedUserName',
          label: 'Assigned user',
          kind: 'text',
          hint: 'The end user at that client — not one of ours, which is why this is free text.',
        },
        {
          name: 'purchaseOrderId',
          label: 'Arrived on PO',
          kind: 'select',
          optional: true,
          placeholder: 'Not recorded',
          choices: (orders.data ?? []).map((o) => ({
            value: String(o.PurchaseOrderId),
            label: `${o.PoNumber} — ${o.Description}`,
          })),
        },
        { name: 'purchaseDate', label: 'Purchased', kind: 'date' },
        { name: 'warrantyExpiry', label: 'Warranty expires', kind: 'date' },
        { name: 'unitCost', label: 'Unit cost', kind: 'money' },
        {
          name: 'quantity',
          label: 'Quantity',
          kind: 'int',
          hint: 'Units on this line. Stock value is this times the unit cost.',
        },
        { name: 'lastAudited', label: 'Last audited', kind: 'date' },
        { name: 'notes', label: 'Notes', kind: 'textarea', wide: true },
      ],
      blank: {
        assetTag: '',
        category: 'laptop',
        manufacturer: '',
        model: '',
        serialNumber: '',
        condition: 'new',
        status: 'in_stock',
        depotLocation: '',
        bin: '',
        assignedClientId: '',
        assignedUserName: '',
        purchaseOrderId: '',
        purchaseDate: '',
        warrantyExpiry: '',
        unitCost: '',
        quantity: 1,
        lastAudited: '',
        notes: '',
      },
      toForm: (row) => ({
        assetTag: row.AssetTag,
        category: row.Category,
        manufacturer: row.Manufacturer ?? '',
        model: row.Model ?? '',
        serialNumber: row.SerialNumber ?? '',
        condition: row.Condition,
        status: row.Status,
        depotLocation: row.DepotLocation ?? '',
        bin: row.Bin ?? '',
        assignedClientId: row.AssignedClientId ?? '',
        assignedUserName: row.AssignedUserName ?? '',
        purchaseOrderId: row.PurchaseOrderId ?? '',
        purchaseDate: row.PurchaseDate?.slice(0, 10) ?? '',
        warrantyExpiry: row.WarrantyExpiry?.slice(0, 10) ?? '',
        unitCost: row.UnitCost,
        quantity: row.Quantity,
        lastAudited: row.LastAudited?.slice(0, 10) ?? '',
        notes: row.Notes,
      }),
    }),
    [clients.data, orders.data],
  );

  const reload = () => {
    analytics.reload();
    onChanged();
  };

  const d = analytics.data;
  const s = d?.summary;

  return (
    <div className="admin-stack">
      {analytics.error && <ErrorBanner message={analytics.error} />}
      {!d && analytics.loading && <Loading label="Loading the depot" />}

      {d && s && s.lineItems > 0 && (
        <>
          <div className="grid grid-4">
            <Stat
              label="Stock value"
              value={formatCompactCurrency(s.stockValue)}
              foot={`${num(s.inStockUnits)} units on the shelf`}
            />
            <Stat
              label="In RMA"
              value={s.rmaOpen}
              flagged={s.rmaOpen > 0}
              foot={s.faulty > 0 ? `${s.faulty} marked faulty` : 'awaiting a decision'}
            />
            <Stat
              label="Warranty ≤90d"
              value={s.warrantyExpiring90}
              foot={
                s.outOfWarranty > 0
                  ? `${s.outOfWarranty} already out of warranty`
                  : 'running out soon'
              }
            />
            <Stat
              label="Audit overdue"
              value={s.auditOverdue90}
              flagged={s.auditOverdue90 > 0}
              /* Never-audited is folded in here on purpose — see the note at the
                 top of this file. */
              foot="stocked, unseen in 90 days or never"
            />
          </div>

          <div className="grid grid-4">
            <Stat label="Line items" value={s.lineItems} foot={`${num(s.totalUnits)} units in total`} />
            <Stat label="Deployed" value={s.deployed} foot="out at a client" />
            <Stat
              label="Awaiting disposal"
              value={s.awaitingDisposal}
              foot="written off, still on site"
            />
            <Stat
              label="Reserved, unassigned"
              value={s.unassignedReserved}
              flagged={s.unassignedReserved > 0}
              /* Reserved for nobody is stock that is neither available nor
                 committed — the state that quietly loses kit. */
              foot="held back for no named client"
            />
          </div>

          <div className="admin-split">
            <ChartCard
              title="Stock by category"
              subtitle="Value on the shelf and out, by kind of kit."
              table={{
                columns: [
                  {
                    key: 'category',
                    label: 'Category',
                    render: (row: DepotData['byCategory'][number]) => snakeLabel(row.category),
                  },
                  { key: 'lineItems', label: 'Lines', align: 'right' },
                  { key: 'units', label: 'Units', align: 'right' },
                  {
                    key: 'value',
                    label: 'Value',
                    align: 'right',
                    render: (row: DepotData['byCategory'][number]) => formatCompactCurrency(row.value),
                  },
                ],
                rows: d.byCategory,
                rowKey: (row) => row.category,
              }}
            >
              <RankedBars
                rows={d.byCategory.map((c) => ({
                  label: snakeLabel(c.category),
                  value: c.value,
                  meta: `${num(c.units)} units`,
                }))}
                format={(v) => formatCompactCurrency(v)}
                labelWidth={132}
              />
            </ChartCard>

            <ChartCard
              title="Where it is"
              subtitle="Units by depot location."
              table={{
                columns: [
                  { key: 'depotLocation', label: 'Location' },
                  { key: 'units', label: 'Units', align: 'right' },
                  {
                    key: 'value',
                    label: 'Value',
                    align: 'right',
                    render: (row: DepotData['byLocation'][number]) => formatCompactCurrency(row.value),
                  },
                ],
                rows: d.byLocation,
                rowKey: (row) => row.depotLocation,
              }}
            >
              <RankedBars
                rows={d.byLocation.map((l) => ({
                  label: l.depotLocation,
                  value: l.units,
                  meta: formatCompactCurrency(l.value),
                }))}
                format={(v) => num(v)}
                labelWidth={148}
              />
            </ChartCard>
          </div>

          <div className="admin-split">
            <ChartCard
              title="Lifecycle"
              subtitle="Units by status — the whole estate, not just the shelf."
              table={{
                columns: [
                  {
                    key: 'status',
                    label: 'Status',
                    render: (row: DepotData['byStatus'][number]) => snakeLabel(row.status),
                  },
                  { key: 'lineItems', label: 'Lines', align: 'right' },
                  { key: 'units', label: 'Units', align: 'right' },
                  {
                    key: 'value',
                    label: 'Value',
                    align: 'right',
                    render: (row: DepotData['byStatus'][number]) => formatCompactCurrency(row.value),
                  },
                ],
                rows: d.byStatus,
                rowKey: (row) => row.status,
              }}
            >
              <RankedBars
                rows={d.byStatus.map((r) => ({
                  label: snakeLabel(r.status),
                  value: r.units,
                  meta: formatCompactCurrency(r.value),
                  /* Two statuses are problems rather than places on a scale, so
                     they take the status ladder while the rest stay one hue. */
                  color:
                    r.status === 'rma'
                      ? 'var(--status-serious)'
                      : r.status === 'awaiting_disposal'
                        ? 'var(--status-warning)'
                        : undefined,
                }))}
                format={(v) => num(v)}
                labelWidth={148}
              />
            </ChartCard>

            <ChartCard
              title="Condition"
              subtitle="Units by condition."
              table={{
                columns: [
                  {
                    key: 'condition',
                    label: 'Condition',
                    render: (row: DepotData['byCondition'][number]) => snakeLabel(row.condition),
                  },
                  { key: 'units', label: 'Units', align: 'right' },
                ],
                rows: d.byCondition,
                rowKey: (row) => row.condition,
              }}
            >
              <RankedBars
                rows={d.byCondition.map((c) => ({
                  label: snakeLabel(c.condition),
                  value: c.units,
                  color: c.condition === 'faulty' ? 'var(--status-serious)' : undefined,
                }))}
                format={(v) => num(v)}
                labelWidth={132}
              />
            </ChartCard>
          </div>

          <Card
            title="Needs a decision"
            subtitle="In RMA, marked faulty, or with a warranty inside 90 days. Soonest warranty first."
          >
            <DataTable
              columns={ATTENTION_COLUMNS}
              rows={d.attention}
              rowKey={(row) => String(row.depotAssetId)}
              empty="Nothing in the depot needs a decision."
            />
          </Card>
        </>
      )}

      <RegisterPanel spec={spec} canWrite={canWrite} onChanged={reload} />
    </div>
  );
}

const ATTENTION_COLUMNS: Column<DepotData['attention'][number]>[] = [
  {
    key: 'assetTag',
    label: 'Asset',
    render: (row) => (
      <div>
        <div className="admin-cell-title">{row.assetTag}</div>
        <div className="xs muted">
          {[row.manufacturer, row.model].filter(Boolean).join(' ') || snakeLabel(row.category)}
        </div>
      </div>
    ),
  },
  {
    key: 'status',
    label: 'Status',
    render: (row) => (
      <div className="admin-due">
        <StatusChip band={assetBand(row.status)}>{snakeLabel(row.status)}</StatusChip>
        {row.condition === 'faulty' && <StatusChip band="serious">faulty</StatusChip>}
      </div>
    ),
  },
  {
    key: 'where',
    label: 'Where',
    render: (row) => (
      <div>
        <div>{row.depotLocation ?? <span className="sub">unassigned</span>}</div>
        {row.assignedClientName && <div className="xs muted">for {row.assignedClientName}</div>}
      </div>
    ),
  },
  { key: 'quantity', label: 'Qty', align: 'right' },
  {
    key: 'lineValue',
    label: 'Value',
    align: 'right',
    render: (row) => formatCompactCurrency(row.lineValue),
  },
  {
    key: 'warrantyExpiry',
    label: 'Warranty',
    render: (row) =>
      row.warrantyExpiry ? (
        <div className="admin-due">
          <span>{formatIsoShort(row.warrantyExpiry)}</span>
          <StatusChip band={urgency(row.daysToWarrantyExpiry)}>
            {relativeDays(row.daysToWarrantyExpiry)}
          </StatusChip>
        </div>
      ) : (
        <span className="sub">not recorded</span>
      ),
  },
];
